import {
	areItemDatesValid,
	createCanvasTimelineDate,
	DRAGGABLE_TYPE,
	getMomentFromCanvasTimelineDate,
	GROUP_TYPE,
	isSimulationMode,
	ITEM_TYPE,
} from '../canvas-timeline/canvas_timeline_util';
import {MODAL_TYPE, showModal} from '../../../forecast-app/shared/components/modals/generic_modal_conductor';
import ProjectAllocationItem from './items/project_allocation_item';
import TaskGroup from './groups/task_group';
import {ESTIMATION_UNIT, PROJECT_STATUS} from '../../../constants';
import TaskItem from './items/task_item';
import PlaceholderGroup from './groups/placeholder_group';
import PhaseGroup from './groups/phase_group';
import PhaseItem from './items/phase_item';
import Util from '../../../forecast-app/shared/util/util';
import ProjectSubGroup from './groups/project_sub_group';
import ProjectGroup from './groups/project_group';
import ProjectItem from './items/project_item';
import {dispatch, EVENT_ID} from '../../../containers/event_manager';
import {getRelevantDependencyTaskIdSet} from './projects_scheduling_dependencies';
import {composeProjectAllocation, composeTaskGroup} from './projects_scheduling_data';
import {hasPermission} from '../../../forecast-app/shared/util/PermissionsUtil';
import {PERMISSION_TYPE} from '../../../Permissions';
import {
	addDependenciesFromTasks,
	addSubTaskToMap,
	changeSubTaskDates,
	findTaskGroupInTree,
	insertTaskInGroupTree,
} from './projects_scheduling_util';
import {removeFromArray} from '../utils';

function removeFromPreviousPhase(previousPhaseId, updatedTask, phasesGroup, phaseGroup) {
	const previousPhaseIdOrNoPhaseId = previousPhaseId ? previousPhaseId : updatedTask.projectId + 'no_milestone';
	const previousPhaseGroup = phasesGroup.groups.find(g => g.id === previousPhaseIdOrNoPhaseId);
	if (previousPhaseGroup && previousPhaseGroup !== phaseGroup) {
		previousPhaseGroup.groups = previousPhaseGroup.groups.filter(group => group.id !== updatedTask.id);
	}
}

const onTaskUpdated = (response, pageComponent, responseTask) => {
	const data = pageComponent.getData();
	const {parentTaskToSubtaskMap} = data;
	const {formatMessage} = pageComponent.props.intl;
	const {items, groups} = pageComponent.state;
	let changedTask;
	const updatedTask = {
		approved: true,
		assignedPersons: responseTask.assignedPersons
			? responseTask.assignedPersons.map(assignedPerson => {
					return assignedPerson.id;
			  })
			: [],
		billable: responseTask.billable,
		blocked: responseTask.blocked,
		bug: responseTask.bug,
		companyTaskId: responseTask.companyTaskId,
		deadlineDay: responseTask.deadlineDay,
		deadlineMonth: responseTask.deadlineMonth,
		deadlineYear: responseTask.deadlineYear,
		deadlineFrom: responseTask.deadlineFrom,
		startFrom: responseTask.startFrom,
		done: responseTask.done,
		estimateForecast: responseTask.estimateForecast,
		estimateForecastMinutes: responseTask.estimateForecastMinutes,
		estimateHigh: responseTask.estimateHigh,
		estimateLow: responseTask.estimateLow,
		highPriority: responseTask.highPriority,
		id: responseTask.id,
		taskLabels: responseTask.taskLabels?.map(t => ({labelId: t.label.id})),
		projectId: responseTask.project.id,
		roleId: responseTask.role ? responseTask.role.id : undefined,
		phaseId: responseTask.phase ? responseTask.phase.id : undefined,
		statusColumnId: responseTask.statusColumnV2?.id,
		progress: responseTask.progress,
		startDay: responseTask.startDay,
		startMonth: responseTask.startMonth,
		startYear: responseTask.startYear,
		timeLeft: responseTask.timeLeft,
		name: responseTask.name,
		fullAccessToProject: true,
		userCanDeleteTask: responseTask.userCanDeleteTask ? responseTask.userCanDeleteTask : undefined,
		userCantDeleteTaskReason: responseTask.userCantDeleteTaskReason,
		parentTaskId: responseTask.parentTaskId,
	};
	const startDate = createCanvasTimelineDate(responseTask.startYear, responseTask.startMonth, responseTask.startDay);
	const endDate = createCanvasTimelineDate(responseTask.deadlineYear, responseTask.deadlineMonth, responseTask.deadlineDay);

	const affectedProject = data.projects.find(project => project.id === updatedTask.projectId);
	affectedProject.completion = responseTask.project.completion;
	affectedProject.remaining = responseTask.project.remaining;
	affectedProject.forecast = responseTask.project.forecast;

	const color = affectedProject.projectGroupId
		? data.projectGroups.find(projectGroup => projectGroup.id === affectedProject.projectGroupId).color
		: affectedProject.projectColor;
	const parentGroup = groups.find(group => group.id === (affectedProject.projectGroupId || affectedProject.id));
	const teamMembersGroup = parentGroup.groups.find(
		group => group.groupType === GROUP_TYPE.PROJECT_SCHEDULING_PROJECT_SUB_GROUP && group.id && group.id.includes('team')
	);
	for (const personId of updatedTask.assignedPersons) {
		const groupId = (affectedProject.projectGroupId || affectedProject.id) + '-' + personId;
		if (parentGroup) {
			const personGroup = teamMembersGroup.groups.find(group => group.id === groupId);
			if (!personGroup) {
				const person = data.persons.find(person => person.id === personId);
				if (!person.clientId) {
					const roleName = person.roleId ? data.roles.find(role => role.id === person.roleId).name : '';
					teamMembersGroup.groups.push(
						pageComponent.createPersonGroup(person, affectedProject, person.roleId, roleName, groupId, color)
					);
				}
			}
		}
	}
	if (response.updateTask || response.updateTasksBulk) {
		changedTask = data.tasks.find(task => task.id === responseTask.id);
		const previousProjectId = changedTask.projectId;
		const previousPhaseId = changedTask.phaseId;
		data.tasks.splice(data.tasks.indexOf(changedTask), 1, updatedTask);
		data.taskMap.set(updatedTask.id, updatedTask);

		if (previousProjectId !== updatedTask.projectId) {
			//Delete dependencies when moving task to another project since it is not possible to have dependencies between tasks in different projects
			let dependencyToDeleteIndex = data.dependencies.findIndex(dependency =>
				[dependency.thisDependsOnTaskId, dependency.taskIdDependsOnThis].includes(updatedTask.id)
			);
			while (dependencyToDeleteIndex >= 0) {
				data.dependencies.splice(dependencyToDeleteIndex, 1);
				dependencyToDeleteIndex = data.dependencies.findIndex(dependency =>
					[dependency.thisDependsOnTaskId, dependency.taskIdDependsOnThis].includes(updatedTask.id)
				);
			}
		}

		if (previousProjectId !== updatedTask.projectId || previousPhaseId !== updatedTask.phaseId) {
			const taskItem = pageComponent.state.items.find(item => item.groupId === updatedTask.id);
			taskItem.data.color = affectedProject.projectColor;
			const projectGroup = pageComponent.getProjectGroups().find(projectGroup => projectGroup.id === previousProjectId);
			if (projectGroup) {
				const phasesGroup = projectGroup.groups.find(subGroup => subGroup.id.includes('phase'));
				const phaseGroupId = previousPhaseId || previousProjectId + 'no_milestone';
				const phaseGroup = phasesGroup.groups.find(phaseGroup => phaseGroup.id === phaseGroupId);
				if (phaseGroup) {
					const taskGroupIndex = phaseGroup.groups.findIndex(taskGroup => taskGroup.id === updatedTask.id);
					if (taskGroupIndex >= 0) {
						phaseGroup.groups.splice(taskGroupIndex, 1);
					}
				}
			}
		}

		const projectGroup = pageComponent.getProjectGroups().find(projectGroup => projectGroup.id === updatedTask.projectId);
		if (projectGroup) {
			const phasesGroup = projectGroup.groups.find(subGroup => subGroup.id.includes('phase'));
			const phaseGroupId = updatedTask.phaseId || updatedTask.projectId + 'no_milestone';
			const phaseGroup = phasesGroup.groups.find(phaseGroup => phaseGroup.id === phaseGroupId);
			removeFromPreviousPhase(previousPhaseId, updatedTask, phasesGroup, phaseGroup);
			if (phaseGroup) {
				const taskGroup = findTaskGroupInTree(phaseGroup, updatedTask.id);
				if (taskGroup) {
					taskGroup.data.name = updatedTask.name;
					taskGroup.data.isStarted = pageComponent.isTaskStarted(updatedTask.statusColumnId);
					taskGroup.data.task = updatedTask;
				} else {
					const composedTaskGroup = composeTaskGroup(
						pageComponent,
						affectedProject,
						updatedTask,
						phaseGroupId,
						parentTaskToSubtaskMap,
						0
					);
					const updatedTaskGroup = new TaskGroup(composedTaskGroup);
					if (!updatedTask.parentTaskId) {
						phaseGroup.groups.push(updatedTaskGroup);
					} else {
						insertTaskInGroupTree(phaseGroup, updatedTaskGroup);
					}
				}
				if (!phaseGroup.expanded) {
					phaseGroup.toggleExpansion();
					pageComponent.expansionMap.set(phaseGroup.id, phaseGroup.expanded);
				}
			}
			if (!phasesGroup.expanded) {
				phasesGroup.toggleExpansion();
				pageComponent.expansionMap.set(phasesGroup.id, phasesGroup.expanded);
			}
		}

		const taskItem = items.find(
			item => item.itemType === ITEM_TYPE.PROJECT_SCHEDULING_TASK && item.groupId === updatedTask.id
		);
		if (taskItem) {
			taskItem.startDate = startDate;
			taskItem.endDate = endDate;
			taskItem.data.phaseId = updatedTask.phaseId;
			//Seems like mutation response is giving wrong progress if nothing relevant to progress is updated
			const previousProgress = taskItem.data.task.progress || 0;
			taskItem.data.task = updatedTask;
			if (taskItem.data.task.progress === undefined) {
				taskItem.data.task.progress = previousProgress;
			}
			// rebind  mouseEnter and mouseLeave to reference new task object
			taskItem.onMouseEnter = pageComponent.onTaskItemMouseEnter.bind(pageComponent, taskItem.data.task);
			taskItem.onMouseLeave = pageComponent.onTaskItemMouseLeave.bind(pageComponent, taskItem.data.task);
			changeSubTaskDates(pageComponent, parentTaskToSubtaskMap, taskItem);
		} else {
			const project = data.projects.find(project => project.id === updatedTask.projectId);
			if (areItemDatesValid(startDate, endDate)) {
				items.push(
					new TaskItem({
						groupId: updatedTask.id,
						startDate,
						endDate,
						draggableType: ['DONE', 'HALTED'].includes(project.status) ? undefined : DRAGGABLE_TYPE.DATE_ONLY,
						color: project.projectColor,
						task: updatedTask,
						projectId: project.id,
						phaseId: updatedTask.phaseId,
						onMoveAttempt: ['DONE', 'HALTED'].includes(project.status)
							? pageComponent.onDoneOrHaltedMoveAttempt.bind(pageComponent)
							: undefined,
						onMouseEnter: pageComponent.onTaskItemMouseEnter.bind(pageComponent, updatedTask),
						onMouseMove: pageComponent.onTaskItemMouseMove.bind(pageComponent),
						onMouseLeave: pageComponent.onTaskItemMouseLeave.bind(pageComponent, updatedTask),
						onMoving: pageComponent.onTaskItemMoving.bind(pageComponent),
						onMoveEnd: pageComponent.onTaskItemMoveEnd.bind(pageComponent),
						allowDependencyCreation: !project.isJiraProject,
						onClick: () => {
							pageComponent.props.showTaskModal(updatedTask.companyTaskId);
						},
						onDraw: pageComponent.onTaskItemDraw.bind(pageComponent, updatedTask),
						onPersonImageLoad: pageComponent.onPersonImageLoad.bind(pageComponent),
					})
				);
			}
		}
	} else {
		data.tasks.push(updatedTask);
		data.taskMap.set(updatedTask.id, updatedTask);
		if (updatedTask.progress === undefined) {
			updatedTask.progress = 0;
		}
		const project = data.projects.find(project => project.id === updatedTask.projectId);
		const projectGroup = pageComponent.getProjectGroups().find(projectGroup => projectGroup.id === updatedTask.projectId);
		if (!projectGroup) return;
		const phasesGroup = projectGroup.groups.find(subGroup => subGroup.id.includes('phase'));
		const phaseGroupId = updatedTask.phaseId || updatedTask.projectId + 'no_milestone';
		let phaseGroup = phasesGroup.groups.find(phaseGroup => phaseGroup.id === phaseGroupId);
		if (!phaseGroup) {
			if (!updatedTask.phaseId) {
				const placeholderGroup = new PlaceholderGroup({
					id: project.id + 'no_milestoneempty',
					name: formatMessage({id: 'scheduling.no_current_tasks'}),
					color: project.projectColor,
					xOffset: project.isInProjectGroup ? 16 : 10,
					showOnlyIfNoSiblingsVisible: true,
					onItemCreate: (startDate, endDate) => {
						showModal({
							type: MODAL_TYPE.CANVAS_CREATE_NEW_MODAL,
							selectedProjectId: project.id,
							estimationUnit: project.estimationUnit,
							roles: data.roles,
							persons: data.persons,
							projects: data.projects,
							phases: data.phases,
							projectPersons: data.projectPersons,
							startDate: getMomentFromCanvasTimelineDate(startDate),
							endDate: getMomentFromCanvasTimelineDate(endDate),
							isProjectScheduling: true,
							viewerId: data.viewer.actualPersonId,
							removeLowHigh: data.viewer.availableFeatureFlags.some(flag => flag.key === 'remove_low_high'),
						});
					},
				});

				phaseGroup = new PhaseGroup({
					id: project.id + 'no_milestone',
					name: formatMessage({id: 'project_scopes.no-scope'}),
					color: project.projectColor,
					isInProjectGroup: project.isInProjectGroup,
					groups: [placeholderGroup],
					onItemCreate: (startDate, endDate) => {
						showModal({
							type: MODAL_TYPE.CANVAS_CREATE_NEW_MODAL,
							selectedProjectId: project.id,
							estimationUnit: project.estimationUnit,
							roles: data.roles,
							persons: data.persons,
							projects: data.projects,
							phases: data.phases,
							projectPersons: data.projectPersons,
							startDate: getMomentFromCanvasTimelineDate(startDate),
							endDate: getMomentFromCanvasTimelineDate(endDate),
							isProjectScheduling: true,
							viewerId: data.viewer.actualPersonId,
							removeLowHigh: data.viewer.availableFeatureFlags.some(flag => flag.key === 'remove_low_high'),
						});
					},
				});
				phasesGroup.groups.push(phaseGroup);
			}
			if (!phaseGroup.expanded) {
				phaseGroup.toggleExpansion();
				pageComponent.expansionMap.set(phaseGroup.id, phaseGroup.expanded);
			}
		}

		if (!phasesGroup.expanded) {
			phasesGroup.toggleExpansion();
			pageComponent.expansionMap.set(phasesGroup.id, phasesGroup.expanded);
		}

		const newTaskGroup = new TaskGroup({
			task: updatedTask,
			id: updatedTask.id,
			companyTaskId: updatedTask.companyTaskId,
			name: updatedTask.name,
			color: project.projectColor,
			isDone: false,
			isStarted: false,
			isInProjectGroup: project.isInProjectGroup,
			hideIfEmpty: !isSimulationMode(),
			forceDrawCondition: () => {
				return getRelevantDependencyTaskIdSet(pageComponent).has(updatedTask.id);
			},
			onClick: () => {
				pageComponent.props.showTaskModal(updatedTask.companyTaskId);
			},
			onItemCreate: (startDate, endDate, group) => {
				showModal({
					type: MODAL_TYPE.CANVAS_CREATE_NEW_MODAL,
					selectedProjectId: project.id,
					selectedPhaseId: updatedTask.phaseId,
					estimationUnit: project.estimationUnit,
					roles: data.roles,
					persons: data.persons,
					projects: data.projects,
					phases: data.phases,
					projectPersons: data.projectPersons,
					startDate: getMomentFromCanvasTimelineDate(startDate),
					endDate: getMomentFromCanvasTimelineDate(endDate),
					isProjectScheduling: true,
					viewerId: data.viewer.actualPersonId,
					removeLowHigh: data.viewer.availableFeatureFlags.some(flag => flag.key === 'remove_low_high'),
				});
			},
		});
		if (!updatedTask.parentTaskId) {
			phaseGroup.groups.push(newTaskGroup);
		} else {
			addSubTaskToMap(parentTaskToSubtaskMap, updatedTask);
			insertTaskInGroupTree(phaseGroup, newTaskGroup);
		}

		if (areItemDatesValid(startDate, endDate)) {
			items.push(
				new TaskItem({
					groupId: updatedTask.id,
					startDate,
					endDate,
					draggableType: ['DONE', 'HALTED'].includes(project.status) ? undefined : DRAGGABLE_TYPE.DATE_ONLY,
					color: project.projectColor,
					task: updatedTask,
					projectId: project.id,
					phaseId: updatedTask.phaseId,
					onMoveAttempt: ['DONE', 'HALTED'].includes(project.status)
						? pageComponent.onDoneOrHaltedMoveAttempt.bind(pageComponent)
						: undefined,
					onMouseEnter: pageComponent.onTaskItemMouseEnter.bind(pageComponent, updatedTask),
					onMouseLeave: pageComponent.onTaskItemMouseLeave.bind(pageComponent, updatedTask),
					onMoving: pageComponent.onTaskItemMoving.bind(pageComponent),
					onMoveEnd: pageComponent.onTaskItemMoveEnd.bind(pageComponent),
					allowDependencyCreation: !project.isJiraProject,
					onClick: () => {
						pageComponent.props.showTaskModal(updatedTask.companyTaskId);
					},
					onDraw: pageComponent.onTaskItemDraw.bind(pageComponent, updatedTask),
					onPersonImageLoad: pageComponent.onPersonImageLoad.bind(pageComponent),
				})
			);
		}
	}

	if (updatedTask.phaseId) {
		const phaseItem = items.find(item => item.groupId === updatedTask.phaseId);
		if (phaseItem) {
			phaseItem.data.completion = pageComponent.getPhaseCompletion(updatedTask.phaseId);
		}
	}

	// Update timeline heatmap cache
	if (pageComponent.props.isProjectTimeline) {
		const affectedPersons = [...updatedTask.assignedPersons];
		if (changedTask) {
			changedTask.assignedPersons.forEach(personId => {
				if (!affectedPersons.includes(personId)) {
					affectedPersons.push(personId);
				}
			});
		}
		// delete cache for affected persons
		affectedPersons.forEach(personId => {
			pageComponent.timelineHeatmapCache.delete(personId);
		});
	}
};

export const handleProjectsSchedulingMutation = (pageComponent, response) => {
	const data = pageComponent.getData();
	const {parentTaskToSubtaskMap} = data;
	const {formatMessage} = pageComponent.props.intl;
	const {items, groups} = pageComponent.state;

	function getTeamMembersGroup(projectOrProjectGroupId) {
		const parentGroup = groups.find(group => group.id === projectOrProjectGroupId);
		if (parentGroup) {
			return parentGroup.groups.find(
				group =>
					group.groupType === GROUP_TYPE.PROJECT_SCHEDULING_PROJECT_SUB_GROUP && group.id && group.id.includes('team')
			);
		}
	}

	function addPersonGroup(personId, projectOrProjectGroupId) {
		const groupId = projectOrProjectGroupId ? `${projectOrProjectGroupId}-${personId}` : `${personId}-2`;
		const project =
			data.projects.find(project => project.id === projectOrProjectGroupId) ||
			data.projectGroups.find(pg => pg.id === projectOrProjectGroupId);
		const color = project.projectColor || project.color || '#efefef';
		const teamMembersGroup = getTeamMembersGroup(projectOrProjectGroupId);
		if (teamMembersGroup) {
			const personGroup = teamMembersGroup.groups.find(group => group.id === groupId);
			if (!personGroup) {
				const person = data.persons.find(person => person.id === personId);
				if (!person.clientId) {
					const roleName = person.roleId ? data.roles.find(role => role.id === person.roleId).name : '';
					teamMembersGroup.groups.push(
						pageComponent.createPersonGroup(person, project, person.roleId, roleName, groupId, color)
					);
				}
			}
		}
	}

	function createOrUpdateAllocation(responseAllocation) {
		const formattedAllocation = {
			id: responseAllocation.id,
			description: responseAllocation.description,
			monday: responseAllocation.monday,
			tuesday: responseAllocation.tuesday,
			wednesday: responseAllocation.wednesday,
			thursday: responseAllocation.thursday,
			friday: responseAllocation.friday,
			saturday: responseAllocation.saturday,
			sunday: responseAllocation.sunday,
			startYear: responseAllocation.startYear,
			startMonth: responseAllocation.startMonth,
			startDay: responseAllocation.startDay,
			endYear: responseAllocation.endYear,
			endMonth: responseAllocation.endMonth,
			endDay: responseAllocation.endDay,
			personId: responseAllocation.person.id,
			projectGroupId: responseAllocation.projectGroupId,
			projectGroupColor: responseAllocation.projectGroupColor,
			projectId: responseAllocation.project ? responseAllocation.project.id : null,
			idleTimeId: responseAllocation.idleTime ? responseAllocation.idleTime.id : null,
			idleTimeName: responseAllocation.idleTime ? responseAllocation.idleTime.name : null,
			isIdleTimeInternal: responseAllocation.idleTime ? responseAllocation.idleTime.isInternalTime : null,
			registerTime: responseAllocation.registerTime,
		};

		const allocationItemData = composeProjectAllocation(pageComponent, formattedAllocation);

		if (allocationItemData) {
			const changedAllocation = data.allocations.find(allocation => allocation.id === responseAllocation.id);

			if (changedAllocation) {
				data.allocations.splice(data.allocations.indexOf(changedAllocation), 1, formattedAllocation);
				// Remove for old person
				const allocationsForOldPerson = data.allocationsByPerson[changedAllocation.personId] || [];
				data.allocationsByPerson[changedAllocation.personId] = allocationsForOldPerson.filter(
					allocation => allocation.id !== changedAllocation.id
				);
				// Add for new person
				const allocationsForNewPerson = data.allocationsByPerson[formattedAllocation.personId] || [];
				data.allocationsByPerson[formattedAllocation.personId] = allocationsForNewPerson;
				allocationsForNewPerson.push(formattedAllocation);

				const allocationItem = items.find(
					item => item.data.allocation && item.data.allocation.id === responseAllocation.id
				);
				if (allocationItem) {
					allocationItem.resetItemRow();
					allocationItem.updateWithData(allocationItemData);
				}
			} else {
				data.allocations.push(formattedAllocation);
				const allocationsForPerson = data.allocationsByPerson[formattedAllocation.personId] || [];
				data.allocationsByPerson[formattedAllocation.personId] = allocationsForPerson;
				allocationsForPerson.push(formattedAllocation);
				items.push(new ProjectAllocationItem(allocationItemData));
			}
			addPersonGroup(formattedAllocation.personId, formattedAllocation.projectGroupId || formattedAllocation.projectId);
		}
	}

	function deleteAllocation(allocationToDelete) {
		const deletedAllocationId = allocationToDelete.id;
		data.allocations.splice(data.allocations.indexOf(allocationToDelete), 1);
		const allocationsForPerson = data.allocationsByPerson[allocationToDelete.personId] || [];
		data.allocationsByPerson[allocationToDelete.personId] = allocationsForPerson.filter(
			allocation => allocation.id !== allocationToDelete.id
		);

		const itemIndex = items.findIndex(item => item.data.allocation && item.data.allocation.id === deletedAllocationId);
		items.splice(itemIndex, 1);
	}

	function createProjectPerson(projectOrProjectGroupId, projectPerson) {
		const personId = projectPerson.person.id;
		const projectId = projectPerson.project.id;
		const person = data.persons.find(person => person.id === personId);
		const newProjectPerson = {
			id: projectPerson.id,
			personId: person.id,
			roleId: person.roleId,
			projectId: projectId,
		};

		const alreadyExists = data.projectPersons.some(pp => pp.personId === person.id && pp.projectId === projectId);

		if (!alreadyExists) {
			data.projectPersons.push(newProjectPerson);
			const projectPersonsOnProject = data.projectPersonsByProject[projectId] || [];
			data.projectPersonsByProject[projectId] = projectPersonsOnProject;
			projectPersonsOnProject.push(newProjectPerson);
		}

		addPersonGroup(personId, projectOrProjectGroupId);
	}

	function deleteProjectPerson(projectId, projectPerson) {
		const projectPersonId = projectPerson.id;
		const personId = projectPerson.personId;
		removeFromArray(data.projectPersons, projectPerson => projectPerson.id === projectPersonId);
		removeFromArray(data.projectPersonsByProject[projectId], projectPerson => projectPerson.id === projectPersonId);

		const project = data.projects.find(project => project.id === projectId);
		const teamMembersGroup = getTeamMembersGroup(project.projectGroupId || project.id);
		if (teamMembersGroup?.groups) {
			removeFromArray(
				teamMembersGroup.groups,
				group => group.id === `${project.projectGroupId || project.id}-${personId}`
			);
		}
	}

	if (response.createAllocation || response.updateAllocation) {
		let responseAllocation = response.updateAllocation
			? response.updateAllocation.allocation
			: response.createAllocation.allocation.node;
		createOrUpdateAllocation(responseAllocation);
	} else if (response.deleteAllocation) {
		if (response.deleteAllocation.errors) return;
		const allocationToDelete = data.allocations.find(
			allocation => allocation.id === response.deleteAllocation.deletedAllocationId
		);
		deleteAllocation(allocationToDelete);
	} else if (response.createMultipleAllocations) {
		const project = response.createMultipleAllocations.project || response.createMultipleAllocations.projectGroup;
		if (project?.allocations?.edges) {
			for (const allocationEdge of project.allocations.edges) {
				createOrUpdateAllocation(allocationEdge.node);
			}
		}
	} else if (response.createProjectPerson) {
		const projectId = response.createProjectPerson.project?.id || response.createProjectPerson.projectGroup?.id;
		if (response.createProjectPerson.projectPersons) {
			for (const projectPerson of response.createProjectPerson.projectPersons) {
				createProjectPerson(projectId, projectPerson);
			}
		}
	} else if (response.deleteProjectPerson) {
		const deletedProjectPersonIds = response.deleteProjectPerson.deletedProjectPersonIds;
		const deletedProjectPersons = data.projectPersons.filter(projectPerson =>
			deletedProjectPersonIds.includes(projectPerson.id)
		);
		for (const deletedProjectPerson of deletedProjectPersons) {
			// In scheduling, for connected projects, allocations are stored on projectGroup instead of project (which is how it is in service-company).
			const projectGroupId = response.deleteProjectPerson.projectGroup?.id;
			const allocationsToDelete = data.allocations.filter(
				allocation =>
					allocation.personId === deletedProjectPerson.personId &&
					(allocation.projectId === deletedProjectPerson.projectId ||
						(projectGroupId && allocation.projectGroupId === projectGroupId))
			);
			for (const allocationToDelete of allocationsToDelete) {
				deleteAllocation(allocationToDelete);
			}
			deleteProjectPerson(deletedProjectPerson.projectId, deletedProjectPerson);
		}
	} else if (response.updateTask || response.createTask || response.duplicateTask) {
		const responseTask =
			response.updateTask && response.updateTask.tasks
				? response.updateTask.tasks[0]
				: response.updateTask && response.updateTask.taskEdge
				? response.updateTask.taskEdge.node
				: response.duplicateTask
				? response.duplicateTask.task.node
				: response.createTask.task.node;
		onTaskUpdated(response, pageComponent, responseTask);
	} else if (response.updateTasksBulk) {
		const updatedTasks = response.updateTasksBulk.tasks;
		for (const responseTask of updatedTasks) {
			onTaskUpdated(response, pageComponent, responseTask);
		}
	} else if (response.deleteTask) {
		const taskToDelete = data.tasks.find(task => task.id === response.deleteTask.deletedTasksIds[0]);
		data.tasks.splice(data.tasks.indexOf(taskToDelete), 1);
		data.taskMap.delete(response.deleteTask.deletedTasksIds[0]);

		const itemIndex = items.findIndex(
			item =>
				item.itemType === ITEM_TYPE.PROJECT_SCHEDULING_TASK && item.groupId === response.deleteTask.deletedTasksIds[0]
		);
		items.splice(itemIndex, 1);

		const affectedProject = data.projects.find(project => project.id === taskToDelete.projectId);
		affectedProject.completion = response.deleteTask.project.completion;
		affectedProject.remaining = response.deleteTask.project.remaining;
		affectedProject.forecast = response.deleteTask.project.forecast;

		if (taskToDelete.phaseId) {
			const phaseItem = items.find(item => item.groupId === taskToDelete.phaseId);
			if (phaseItem) {
				phaseItem.data.completion = pageComponent.getPhaseCompletion(taskToDelete.phaseId);
			}
		}

		// Update timeline heatmap cache
		if (pageComponent.props.isProjectTimeline) {
			const affectedPersons = taskToDelete.assignedPersons;
			// delete cache for affected persons
			affectedPersons.forEach(personId => {
				pageComponent.timelineHeatmapCache.delete(personId);
			});
		}

		const projectGroup = pageComponent.getProjectGroups().find(projectGroup => projectGroup.id === taskToDelete.projectId);
		if (!projectGroup) return;
		const phaseGroup = projectGroup.groups[0].groups.find(phaseGroup => {
			return phaseGroup.id === (taskToDelete.phaseId || `${taskToDelete.projectId}no_milestone`);
		});
		if (!phaseGroup) return;
		const taskGroupIndex = phaseGroup.groups.findIndex(taskGroup => taskGroup.id === taskToDelete.id);
		if (taskGroupIndex > -1) {
			phaseGroup.groups.splice(taskGroupIndex, 1);
		}
	} else if (response.createTimeReg || response.updateTimeReg) {
		const responseTimeRegistration = response.updateTimeReg
			? response.updateTimeReg.timeReg
			: response.createTimeReg.timeReg.node;
		const updatedTimeRegistration = {
			id: responseTimeRegistration.id,
			day: responseTimeRegistration.day,
			month: responseTimeRegistration.month,
			year: responseTimeRegistration.year,
			minutesRegistered: responseTimeRegistration.minutesRegistered,
			personId: responseTimeRegistration.person.id,
			taskId: responseTimeRegistration.task && responseTimeRegistration.task.id,
			projectId: responseTimeRegistration.project && responseTimeRegistration.project.id,
			idleTimeId: responseTimeRegistration.idleTime && responseTimeRegistration.idleTime.id,
			isIdleTimeInternal: responseTimeRegistration.idleTime ? responseTimeRegistration.idleTime.isInternalTime : null,
		};

		if (updatedTimeRegistration.taskId) {
			const changedTask = data.tasks.find(task => task.id === updatedTimeRegistration.taskId);
			changedTask.progress = responseTimeRegistration.task.progress;
			changedTask.timeLeft = responseTimeRegistration.task.timeLeft;

			const affectedProject = data.projects.find(project => project.id === changedTask.projectId);
			affectedProject.remaining -= responseTimeRegistration.minutesRegistered;
			affectedProject.reported += responseTimeRegistration.minutesRegistered;
			if (affectedProject.completion < 100) {
				affectedProject.completion += affectedProject.reported / affectedProject.forecast;
				if (affectedProject.completion >= 100) {
					affectedProject.completion = 99;
				}
			}

			const parentTimeRegistrationArray = data.parentTimeRegistrationMap.get(changedTask.id);

			if (response.updateTimeReg) {
				const index = parentTimeRegistrationArray.findIndex(
					timeRegistration => timeRegistration.id === responseTimeRegistration.id
				);
				if (index > -1 && parentTimeRegistrationArray) {
					parentTimeRegistrationArray.splice(index, 1, updatedTimeRegistration);
				}
			} else {
				if (parentTimeRegistrationArray) {
					parentTimeRegistrationArray.push(updatedTimeRegistration);
				} else {
					data.parentTimeRegistrationMap.set(changedTask.id, [updatedTimeRegistration]);
				}
			}

			if (changedTask.phaseId) {
				const phaseItem = items.find(item => item.groupId === changedTask.phaseId);
				if (phaseItem) {
					phaseItem.data.completion = pageComponent.getPhaseCompletion(changedTask.phaseId);
				}
			}
		}

		if (response.updateTimeReg) {
			const changedTimeRegistration = data.timeRegistrations.find(
				timeRegistration => timeRegistration.id === responseTimeRegistration.id
			);
			data.timeRegistrations.splice(data.timeRegistrations.indexOf(changedTimeRegistration), 1, updatedTimeRegistration);
		} else {
			data.timeRegistrations.push(updatedTimeRegistration);
		}
		// Update timeline heatmap cache
		if (pageComponent.props.isProjectTimeline && !data.company.isUsingSchedulingPlanMode) {
			pageComponent.timelineHeatmapCache.delete(updatedTimeRegistration.personId);
		}
	} else if (response.deleteTimeReg) {
		const timeRegistrationToDelete = data.timeRegistrations.find(
			timeRegistration => timeRegistration.id === response.deleteTimeReg.deletedTimeRegId
		);

		if (!timeRegistrationToDelete) return;
		data.timeRegistrations.splice(data.timeRegistrations.indexOf(timeRegistrationToDelete), 1);

		const changedTask = data.tasks.find(task => task.id === timeRegistrationToDelete.taskId);
		if (changedTask) {
			changedTask.progress = response.deleteTimeReg.task.progress;
			changedTask.timeLeft = response.deleteTimeReg.task.timeLeft;

			const affectedProject = data.projects.find(project => project.id === changedTask.projectId);
			affectedProject.completion = response.deleteTimeReg.task.project.completion;
			affectedProject.remaining = response.deleteTimeReg.task.project.remaining;
			affectedProject.forecast = response.deleteTimeReg.task.project.forecast;

			const parentTimeRegistrationArray = data.parentTimeRegistrationMap.get(changedTask.id) || [];
			const index = parentTimeRegistrationArray.indexOf(timeRegistrationToDelete);

			if (index > -1) {
				parentTimeRegistrationArray.splice(index, 1);
			}

			if (changedTask.phaseId) {
				const phaseItem = items.find(item => item.groupId === changedTask.phaseId);
				phaseItem.data.completion = pageComponent.getPhaseCompletion(changedTask.phaseId);
			}
		}
		// Update timeline heatmap cache
		if (pageComponent.props.isProjectTimeline && !data.company.isUsingSchedulingPlanMode) {
			pageComponent.timelineHeatmapCache.delete(timeRegistrationToDelete.personId);
		}
	} else if (response.createPhase) {
		const responsePhase = response.createPhase.phase.node;
		const phase = {
			id: responsePhase.id,
			deadlineDay: responsePhase.deadlineDay,
			deadlineMonth: responsePhase.deadlineMonth,
			deadlineYear: responsePhase.deadlineYear,
			startDay: responsePhase.startDay,
			startMonth: responsePhase.startMonth,
			startYear: responsePhase.startYear,
			name: responsePhase.name,
			projectId: response.createPhase.project.id,
		};
		data.phases.push(phase);

		const project = data.projects.find(project => project.id === phase.projectId);
		const isDoneOrHalted = ['DONE', 'HALTED'].includes(project.status);
		const projectGroup = pageComponent.getProjectGroups().find(projectGroup => projectGroup.id === phase.projectId);
		if (!projectGroup) return;
		const phasesGroup = projectGroup.groups.find(subGroup => subGroup.id.includes('phase'));

		phasesGroup.data.phases.push(phase);

		const placeholderGroup = new PlaceholderGroup({
			id: phase.id + 'empty',
			name: formatMessage({id: 'scheduling.no_current_tasks'}),
			color: project.projectColor,
			xOffset: project.isInProjectGroup ? 16 : 10,
			showOnlyIfNoSiblingsVisible: true,
			onItemCreate: isDoneOrHalted
				? undefined
				: (startDate, endDate) => {
						showModal({
							type: MODAL_TYPE.CANVAS_CREATE_NEW_MODAL,
							selectedProjectId: project.id,
							selectedPhaseId: phase.id,
							estimationUnit: project.estimationUnit,
							roles: data.roles,
							persons: data.persons,
							projects: data.projects,
							phases: data.phases,
							projectPersons: data.projectPersons,
							startDate: getMomentFromCanvasTimelineDate(startDate),
							endDate: getMomentFromCanvasTimelineDate(endDate),
							isProjectScheduling: true,
							viewerId: data.viewer.actualPersonId,
							removeLowHigh: data.viewer.availableFeatureFlags.some(flag => flag.key === 'remove_low_high'),
						});
				  },
		});

		const phaseGroup = new PhaseGroup({
			phase,
			id: phase.id,
			name: `${phase.name}`,
			color: project.projectColor,
			isInProjectGroup: project.isInProjectGroup,
			groups: [placeholderGroup],
			onItemCreate: isDoneOrHalted
				? undefined
				: (startDate, endDate) => {
						showModal({
							type: MODAL_TYPE.CANVAS_CREATE_NEW_MODAL,
							selectedProjectId: project.id,
							selectedPhaseId: phase.id,
							estimationUnit: project.estimationUnit,
							roles: data.roles,
							persons: data.persons,
							projects: data.projects,
							phases: data.phases,
							projectPersons: data.projectPersons,
							startDate: getMomentFromCanvasTimelineDate(startDate),
							endDate: getMomentFromCanvasTimelineDate(endDate),
							isProjectScheduling: true,
							viewerId: data.viewer.actualPersonId,
							removeLowHigh: data.viewer.availableFeatureFlags.some(flag => flag.key === 'remove_low_high'),
						});
				  },
			onMouseEnter: pageComponent.onPhaseMouseEnter.bind(pageComponent, phase),
			onMouseLeave: pageComponent.onPhaseMouseLeave.bind(pageComponent),
		});

		phasesGroup.groups.push(phaseGroup);

		const phaseStartDate = createCanvasTimelineDate(phase.startYear, phase.startMonth, phase.startDay);
		const phaseEndDate = createCanvasTimelineDate(phase.deadlineYear, phase.deadlineMonth, phase.deadlineDay);

		if (!phasesGroup.expanded) {
			phasesGroup.toggleExpansion();
			pageComponent.expansionMap.set(phasesGroup.id, phasesGroup.expanded);
		}
		phaseGroup.toggleExpansion();
		pageComponent.expansionMap.set(phaseGroup.id, phaseGroup.expanded);

		if (areItemDatesValid(phaseStartDate, phaseEndDate)) {
			items.push(
				new PhaseItem({
					groupId: phase.id,
					phaseName: phase.name,
					startDate: phaseStartDate,
					endDate: phaseEndDate,
					onMoveAttempt: isDoneOrHalted ? pageComponent.onDoneOrHaltedMoveAttempt.bind(pageComponent) : undefined,
					onMoving: pageComponent.onPhaseItemMoving.bind(pageComponent),
					onMoveEnd: pageComponent.onPhaseItemMoveEnd.bind(pageComponent),
					draggableType:
						!hasPermission(PERMISSION_TYPE.MILESTONE_UPDATE) || isDoneOrHalted
							? undefined
							: DRAGGABLE_TYPE.DATE_ONLY,
					color: project.projectColor,
					completion: 0,
					projectId: project.id,
					isHourEstimated: project.estimationUnit === ESTIMATION_UNIT.HOURS,
					onMouseEnter: pageComponent.onDistributionItemMouseEnter.bind(
						pageComponent,
						ITEM_TYPE.PROJECT_SCHEDULING_PHASE,
						project.id,
						phase.id
					),
					onMouseLeave: pageComponent.onDistributionItemMouseLeave.bind(pageComponent),
					onMouseMove: pageComponent.onDistributionItemMouseMove.bind(pageComponent),
					onClick:
						project.useManualAllocations && project.isInProjectGroup
							? undefined
							: pageComponent.onDistributionItemClick.bind(
									pageComponent,
									ITEM_TYPE.PROJECT_SCHEDULING_PHASE,
									project.id,
									phase.id
							  ),
				})
			);
		}
	} else if (response.deleteDependency) {
		const index = data.dependencies.findIndex(
			dependency => dependency.id === response.deleteDependency.deletedDependencyId
		);
		if (index >= 0) {
			data.dependencies.splice(index, 1);
		}
		pageComponent.setState({dependencies: data.dependencies});
	} else if (response.createDependency) {
		if (response.createDependency.errors) return;
		const dependency = response.createDependency.dependency.node;
		data.dependencies.push({
			id: dependency.id,
			thisDependsOnTaskId: response.createDependency.dependency.node.thisDependsOnTask.id,
			taskIdDependsOnThis: response.createDependency.dependency.node.taskDependsOnThis.id,
			type: dependency.type,
		});
		pageComponent.setState({dependencies: data.dependencies});
	} else if (response.updateDependency) {
		if (response.updateDependency.errors) return;
		const dependency = data.dependencies.find(dependency => dependency.id === response.updateDependency.dependency.id);
		if (!dependency) return;
		dependency.type = response.updateDependency.dependency.type;
		dependency.thisDependsOnTaskId = response.updateDependency.dependency.thisDependsOnTask.id;
		dependency.taskIdDependsOnThis = response.updateDependency.dependency.taskDependsOnThis.id;
		pageComponent.setState({dependencies: data.dependencies});
	} else if (response.createClient) {
		const client = response.createClient.client.node;
		data.clients.push({
			id: client.id,
			name: client.name,
		});
	} else if (response.createMultipleAllocations) {
		const project = response.createMultipleAllocations.project;
		if (project?.allocations?.edges) {
			for (const allocationEdge of project.allocations.edges) {
				createOrUpdateAllocation(allocationEdge.node);
			}
		}
	} else if (response.createProject || response.duplicateProject) {
		if (pageComponent.props.isProjectTimeline) return;
		const project = response.duplicateProject
			? response.duplicateProject.project.node
			: response.createProject.project.node;
		const alreadyExists = data.projects.find(dataProject => dataProject.id === project.id); //We get the response twice when creating project allocation project, ensure not to add things twice

		if (alreadyExists) return;

		const formattedProject = {
			id: project.id,
			status: project.status,
			companyProjectId: project.companyProjectId,
			name: project.name,
			projectColor: project.projectColor,
			useManualAllocations: project.useManualAllocations,
			completion: project.completion,
			remaining: project.remaining,
			projectStartDay: project.projectStartDay,
			projectStartMonth: project.projectStartMonth,
			projectStartYear: project.projectStartYear,
			projectEndDay: project.projectEndDay,
			projectEndMonth: project.projectEndMonth,
			projectEndYear: project.projectEndYear,
			estimationUnit: project.estimationUnit,
			minutesPerEstimationPoint: project.minutesPerEstimationPoint,
			isInProjectGroup: project.isInProjectGroup,
			projectGroupId: project.projectGroupId,
			forecast: project.forecast,
			clientId: project.client ? project.client.id : null,
		};
		data.projects.push(formattedProject);
		data.projectMap.set(project.id, formattedProject);
		const addedProjectStatus = {
			color: '#33cc33',
			projectId: project.id,
		};
		data.projectStatuses.push(addedProjectStatus);
		data.projectStatusMap.set(project.id, addedProjectStatus);
		const clientName = project.client ? data.clients.find(client => client.id === project.client.id).name : '';

		const phaseGroups = [];

		if (project.projectLabels) {
			for (const projectLabelEdge of project.projectLabels.edges) {
				const projectLabel = projectLabelEdge.node;
				const addedProjectLabel = {
					id: projectLabel.id,
					labelId: projectLabel.label.id,
					projectId: project.id,
				};
				data.projectLabels.push(addedProjectLabel);
				const projectLabelsOnProject = data.projectLabelsByProject[project.id] || [];
				data.projectLabelsByProject[project.id] = projectLabelsOnProject;
				projectLabelsOnProject.push(addedProjectLabel);
			}
		}

		if (project.tasks) {
			addDependenciesFromTasks(project.tasks.edges, data.dependencies);

			for (const taskEdge of project.tasks.edges) {
				const task = taskEdge.node;
				const taskData = {
					projectId: project.id,
					approved: true,
					assignedPersons: task.assignedPersons.map(assignedPerson => {
						return assignedPerson.id;
					}),
					billable: task.billable,
					blocked: task.blocked,
					bug: task.bug,
					companyTaskId: task.companyTaskId,
					deadlineDay: task.deadlineDay,
					deadlineMonth: task.deadlineMonth,
					deadlineYear: task.deadlineYear,
					deadlineFrom: task.deadlineFrom,
					startFrom: task.startFrom,
					done: task.done,
					estimateForecast: task.estimateForecast,
					highPriority: task.highPriority,
					id: task.id,
					taskLabels: task.taskLabels?.map(t => ({labelId: t.label.id})),
					roleId: task.role ? task.role.id : undefined,
					phaseId: task.phase ? task.phase.id : undefined,
					startDay: task.startDay,
					startMonth: task.startMonth,
					startYear: task.startYear,
					timeLeft: task.timeLeft,
					name: task.name,
					fullAccessToProject: true,
					parentTaskId: task.parentTaskId,
				};
				data.tasks.push(taskData);
				data.taskMap.set(taskData.id, taskData);
			}
		}

		if (project.phases) {
			for (const phaseEdge of project.phases.edges) {
				const phase = phaseEdge.node;
				let phaseRemaining = 0;
				let phaseTimeRegistered = 0;
				let phaseTimeRegisteredNotDone = 0;
				let phaseForecast = 0;
				let phaseDoneForecast = 0;
				let phaseDoneTasks = 0;
				let phaseTotalTasks = 0;

				data.phases.push({
					id: phase.id,
					deadlineDay: phase.deadlineDay,
					deadlineMonth: phase.deadlineMonth,
					deadlineYear: phase.deadlineYear,
					startDay: phase.startDay,
					startMonth: phase.startMonth,
					startYear: phase.startYear,
					name: phase.name,
					projectId: project.id,
				});

				const taskGroups = [];
				if (project.tasks) {
					// Update sub task map with new tasks
					project.tasks.edges.forEach(taskEdge => {
						const task = taskEdge.node;
						if (task.parentTaskId) {
							addSubTaskToMap(parentTaskToSubtaskMap, task);
						}
					});
					for (const taskEdge of project.tasks.edges.filter(
						taskEdge => taskEdge.node.phase && taskEdge.node.phase.id === phase.id
					)) {
						const projectTask = taskEdge.node;
						++phaseTotalTasks;
						if (projectTask.done) {
							++phaseDoneTasks;
						}

						// Use task from taskMap to preserve task reference
						const task = data.taskMap.get(projectTask.id);

						phaseRemaining += task.done ? 0 : task.timeLeft;
						phaseTimeRegistered += (data.parentTimeRegistrationMap.get(task.id) || []).reduce(
							(total, timeRegistration) => total + timeRegistration.minutesRegistered,
							0
						);
						if (!task.done) {
							phaseTimeRegisteredNotDone += (data.parentTimeRegistrationMap.get(task.id) || []).reduce(
								(total, timeRegistration) => total + timeRegistration.minutesRegistered,
								0
							);
						}
						phaseForecast += task.estimateForecast;
						if (task.done) {
							phaseDoneForecast += task.estimateForecast;
						}
						if (!task.parentTaskId) {
							const composedTaskGroup = composeTaskGroup(
								pageComponent,
								project,
								task,
								phase.id,
								parentTaskToSubtaskMap,
								0
							);
							taskGroups.push(new TaskGroup(composedTaskGroup));
						}
						const startDate = createCanvasTimelineDate(task.startYear, task.startMonth, task.startDay);
						const endDate = createCanvasTimelineDate(task.deadlineYear, task.deadlineMonth, task.deadlineDay);
						if (!areItemDatesValid(startDate, endDate)) continue;
						items.push(
							new TaskItem({
								groupId: task.id,
								startDate,
								endDate,
								draggableType: ['DONE', 'HALTED'].includes(project.status)
									? undefined
									: DRAGGABLE_TYPE.DATE_ONLY,
								color: project.projectColor,
								task: task,
								projectId: project.id,
								phaseId: task.phaseId,
								onMouseEnter: pageComponent.onTaskItemMouseEnter.bind(pageComponent, task),
								onMouseLeave: pageComponent.onTaskItemMouseLeave.bind(pageComponent, task),
								onMoving: pageComponent.onTaskItemMoving.bind(pageComponent),
								onMoveEnd: pageComponent.onTaskItemMoveEnd.bind(pageComponent),
								allowDependencyCreation: !project.isJiraProject,
								onClick: () => {
									pageComponent.props.showTaskModal(task.companyTaskId);
								},
								onDraw: pageComponent.onTaskItemDraw.bind(pageComponent, task),
								onPersonImageLoad: pageComponent.onPersonImageLoad.bind(pageComponent),
							})
						);
					}
				}

				taskGroups.push(
					new PlaceholderGroup({
						id: phase.id + 'empty',
						name: formatMessage({id: 'scheduling.no_current_tasks'}),
						xOffset: 10,
						showOnlyIfNoSiblingsVisible: true,
						color: project.projectColor,
						onItemCreate: (startDate, endDate) => {
							showModal({
								type: MODAL_TYPE.CANVAS_CREATE_NEW_MODAL,
								selectedProjectId: project.id,
								selectedPhaseId: phase.id,
								estimationUnit: project.estimationUnit,
								roles: data.roles,
								persons: data.persons,
								projects: data.projects,
								phases: data.phases,
								projectPersons: data.projectPersons,
								startDate: getMomentFromCanvasTimelineDate(startDate),
								endDate: getMomentFromCanvasTimelineDate(endDate),
								isProjectScheduling: true,
								viewerId: data.viewer.actualPersonId,
								removeLowHigh: data.viewer.availableFeatureFlags.some(flag => flag.key === 'remove_low_high'),
							});
						},
					})
				);

				phaseGroups.push(
					new PhaseGroup({
						phase,
						id: phase.id,
						name: `${phase.name}`,
						color: project.projectColor,
						isInProjectGroup: false,
						groups: taskGroups,
						onItemCreate: (startDate, endDate) => {
							showModal({
								type: MODAL_TYPE.CANVAS_CREATE_NEW_MODAL,
								selectedProjectId: project.id,
								selectedPhaseId: phase.id,
								estimationUnit: project.estimationUnit,
								roles: data.roles,
								persons: data.persons,
								projects: data.projects,
								phases: data.phases,
								projectPersons: data.projectPersons,
								startDate: getMomentFromCanvasTimelineDate(startDate),
								endDate: getMomentFromCanvasTimelineDate(endDate),
								isProjectScheduling: true,
								viewerId: data.viewer.actualPersonId,
								removeLowHigh: data.viewer.availableFeatureFlags.some(flag => flag.key === 'remove_low_high'),
							});
						},
						onMouseEnter: pageComponent.onPhaseMouseEnter.bind(pageComponent, phase),
						onMouseLeave: pageComponent.onPhaseMouseLeave.bind(pageComponent),
					})
				);

				const startDate = createCanvasTimelineDate(phase.startYear, phase.startMonth, phase.startDay);
				const endDate = createCanvasTimelineDate(phase.deadlineYear, phase.deadlineMonth, phase.deadlineDay);
				if (!areItemDatesValid(startDate, endDate)) continue;
				if (project.estimationUnit === ESTIMATION_UNIT.POINTS) {
					phaseRemaining *= project.minutesPerEstimationPoint;
					phaseTimeRegisteredNotDone *= project.minutesPerEstimationPoint;
				}
				let completion;
				if (data.viewer.availableFeatureFlags.find(flag => flag.key === 'use_new_progress_calc')) {
					completion =
						Util.calculateTaskGroupCompletion(
							phaseDoneForecast,
							phaseTimeRegisteredNotDone,
							phaseRemaining,
							phaseDoneTasks,
							phaseTotalTasks
						) * 100;
				} else {
					completion = Util.deprecated_calculateElementProgress(
						phaseForecast,
						phaseTimeRegistered,
						phaseRemaining,
						phaseTotalTasks,
						phaseDoneTasks,
						project.estimationUnit === ESTIMATION_UNIT.HOURS,
						project.minutesPerEstimationPoint,
						false,
						false
					);
				}

				items.push(
					new PhaseItem({
						groupId: phase.id,
						startDate,
						endDate,
						onMoving: pageComponent.onPhaseItemMoving.bind(pageComponent),
						onMoveEnd: pageComponent.onPhaseItemMoveEnd.bind(pageComponent),
						draggableType: DRAGGABLE_TYPE.DATE_ONLY,
						color: project.projectColor,
						completion,
						projectId: project.id,
						isHourEstimated: project.estimationUnit === ESTIMATION_UNIT.HOURS,
						onMouseEnter: pageComponent.onDistributionItemMouseEnter.bind(
							pageComponent,
							ITEM_TYPE.PROJECT_SCHEDULING_PHASE,
							project.id,
							phase.id
						),
						onMouseLeave: pageComponent.onDistributionItemMouseLeave.bind(pageComponent),
						onMouseMove: pageComponent.onDistributionItemMouseMove.bind(pageComponent),
						onClick: pageComponent.onDistributionItemClick.bind(
							pageComponent,
							ITEM_TYPE.PROJECT_SCHEDULING_PHASE,
							project.id,
							phase.id
						),
					})
				);
			}
		}

		pageComponent.sortPhaseGroupsByDate(phaseGroups);

		if (project.phases && project.tasks && project.tasks.edges.find(taskEdge => !taskEdge.node.phase)) {
			const taskGroups = [];

			for (const taskEdge of project.tasks.edges.filter(taskEdge => !taskEdge.node.phase)) {
				const task = taskEdge.node;
				const phaseId = task.phase ? task.phase.id : undefined;
				const formattedTask = {
					projectId: project.id,
					approved: true,
					assignedPersons: task.assignedPersons.map(assignedPerson => {
						return assignedPerson.id;
					}),
					billable: task.billable,
					blocked: task.blocked,
					bug: task.bug,
					companyTaskId: task.companyTaskId,
					deadlineDay: task.deadlineDay,
					deadlineMonth: task.deadlineMonth,
					deadlineYear: task.deadlineYear,
					deadlineFrom: task.deadlineFrom,
					startFrom: task.startFrom,
					done: task.done,
					estimateForecast: task.estimateForecast,
					highPriority: task.highPriority,
					id: task.id,
					taskLabels: task.taskLabels?.map(t => ({labelId: t.label.id})),
					roleId: task.role ? task.role.id : undefined,
					phaseId: phaseId,
					startDay: task.startDay,
					startMonth: task.startMonth,
					startYear: task.startYear,
					timeLeft: task.timeLeft,
					name: task.name,
					fullAccessToProject: true,
				};
				const composedTaskGroup = composeTaskGroup(pageComponent, project, task, phaseId, parentTaskToSubtaskMap, 0);
				taskGroups.push(new TaskGroup(composedTaskGroup));

				const startDate = createCanvasTimelineDate(task.startYear, task.startMonth, task.startDay);
				const endDate = createCanvasTimelineDate(task.deadlineYear, task.deadlineMonth, task.deadlineDay);
				if (!areItemDatesValid(startDate, endDate)) continue;
				items.push(
					new TaskItem({
						groupId: task.id,
						startDate,
						endDate,
						draggableType: DRAGGABLE_TYPE.DATE_ONLY,
						color: project.projectColor,
						task: formattedTask,
						projectId: project.id,
						phaseId: null,
						onMouseEnter: pageComponent.onTaskItemMouseEnter.bind(pageComponent, formattedTask),
						onMouseLeave: pageComponent.onTaskItemMouseLeave.bind(pageComponent, formattedTask),
						onMoving: pageComponent.onTaskItemMoving.bind(pageComponent),
						onMoveEnd: pageComponent.onTaskItemMoveEnd.bind(pageComponent),
						allowDependencyCreation: !project.isJiraProject,
						onClick: () => {
							pageComponent.props.showTaskModal(task.companyTaskId);
						},
						onDraw: pageComponent.onTaskItemDraw.bind(pageComponent, formattedTask),
						onPersonImageLoad: pageComponent.onPersonImageLoad.bind(pageComponent),
					})
				);
			}

			taskGroups.push(
				new PlaceholderGroup({
					id: project.id + 'no_milestoneempty',
					name: formatMessage({id: 'scheduling.no_current_tasks'}),
					xOffset: 10,
					showOnlyIfNoSiblingsVisible: true,
					color: project.projectColor,
					onItemCreate: (startDate, endDate) => {
						showModal({
							type: MODAL_TYPE.CANVAS_CREATE_NEW_MODAL,
							selectedProjectId: project.id,
							estimationUnit: project.estimationUnit,
							roles: data.roles,
							persons: data.persons,
							projects: data.projects,
							phases: data.phases,
							projectPersons: data.projectPersons,
							startDate: getMomentFromCanvasTimelineDate(startDate),
							endDate: getMomentFromCanvasTimelineDate(endDate),
							isProjectScheduling: true,
							viewerId: data.viewer.actualPersonId,
							removeLowHigh: data.viewer.availableFeatureFlags.some(flag => flag.key === 'remove_low_high'),
						});
					},
				})
			);

			phaseGroups.push(
				new PhaseGroup({
					id: project.id + 'no_milestone',
					name: formatMessage({id: 'project_scopes.no-scope'}),
					color: project.projectColor,
					isInProjectGroup: project.isInProjectGroup,
					groups: taskGroups,
					onItemCreate: (startDate, endDate) => {
						showModal({
							type: MODAL_TYPE.CANVAS_CREATE_NEW_MODAL,
							selectedProjectId: project.id,
							estimationUnit: project.estimationUnit,
							roles: data.roles,
							persons: data.persons,
							projects: data.projects,
							phases: data.phases,
							projectPersons: data.projectPersons,
							startDate: getMomentFromCanvasTimelineDate(startDate),
							endDate: getMomentFromCanvasTimelineDate(endDate),
							isProjectScheduling: true,
							viewerId: data.viewer.actualPersonId,
							removeLowHigh: data.viewer.availableFeatureFlags.some(flag => flag.key === 'remove_low_high'),
						});
					},
				})
			);
		}

		const phasesSubGroup = new ProjectSubGroup({
			id: project.id + 'phases',
			name: formatMessage({id: 'common.milestones'}),
			color: project.projectColor,
			isInProjectGroup: false,
			projectGroupColor: null,
			phases: project.phases ? project.phases.edges.map(phaseEdge => phaseEdge.node) : [],
			groups: phaseGroups,
			onMouseEnter: pageComponent.onPhasesGroupMouseEnter.bind(pageComponent, []),
			onMouseLeave: pageComponent.onPhasesGroupMouseLeave.bind(pageComponent),
			onItemCreate: (startDate, endDate) => {
				showModal({
					type: MODAL_TYPE.CANVAS_CREATE_NEW_MODAL,
					selectedProjectId: project.id,
					estimationUnit: project.estimationUnit,
					roles: data.roles,
					persons: data.persons,
					projects: data.projects,
					phases: data.phases,
					projectPersons: data.projectPersons,
					startDate: getMomentFromCanvasTimelineDate(startDate),
					endDate: getMomentFromCanvasTimelineDate(endDate),
					isProjectScheduling: true,
					isMilestoneTabSelected: true,
					viewerId: data.viewer.actualPersonId,
					removeLowHigh: data.viewer.availableFeatureFlags.some(flag => flag.key === 'remove_low_high'),
				});
			},
		});

		const teamMemberGroups = [];
		if (project.projectPersons) {
			for (const projectPersonEdge of project.projectPersons.edges) {
				const person = data.persons.find(person => person.id === projectPersonEdge.node.person.id);
				if (person && !person.clientId) {
					const addedProjectPerson = {
						id: projectPersonEdge.node.id,
						personId: person.id,
						roleId: person.roleId,
						projectId: project.id,
					};
					data.projectPersons.push(addedProjectPerson);
					const projectPersonsOnProject = data.projectPersonsByProject[project.id] || [];
					data.projectPersonsByProject[project.id] = projectPersonsOnProject;
					projectPersonsOnProject.push(addedProjectPerson);
					const roleName = person.roleId ? data.roles.find(role => role.id === person.roleId).name : '';
					teamMemberGroups.push(pageComponent.createPersonGroup(person, project, person.roleId, roleName));
				}
			}
		}

		const teamMembersSubGroup = new ProjectSubGroup({
			id: project.id + 'team-members',
			name: formatMessage({id: 'scheduling.project_scheduling.project_team'}),
			color: project.projectColor,
			projectGroupTeam: false,
			projectGroupColor: null,
			onMouseEnter: pageComponent.onTeamMembersMouseEnter.bind(pageComponent, project),
			onMouseLeave: pageComponent.onTeamMembersMouseLeave.bind(pageComponent, project),
			groups: teamMemberGroups,
		});
		const projectGroup = new ProjectGroup({
			id: project.id,
			companyProjectId: project.companyProjectId,
			name: project.name,
			budgetType: project.budgetType,
			color: project.projectColor,
			status: project.status,
			clientId: project.client ? project.client.id : null,
			clientName,
			groups: [phasesSubGroup, teamMembersSubGroup],
			labelIds: project.projectLabels
				? project.projectLabels.edges.map(projectLabelEdge => projectLabelEdge.node.label.id)
				: [],
			hideIfEmpty: false,
			drawIfAnyChildVisible: true,
			onMouseEnter: pageComponent.onProjectGroupMouseEnter.bind(pageComponent, project),
			onMouseLeave: pageComponent.onProjectGroupMouseLeave.bind(pageComponent, project),
		});
		pageComponent.state.groups.push(projectGroup);

		const startDate = createCanvasTimelineDate(
			project.projectStartYear,
			project.projectStartMonth,
			project.projectStartDay
		);
		const endDate = createCanvasTimelineDate(project.projectEndYear, project.projectEndMonth, project.projectEndDay);
		if (areItemDatesValid(startDate, endDate)) {
			pageComponent.state.items.push(
				new ProjectItem({
					groupId: project.id,
					startDate,
					endDate,
					draggableType: DRAGGABLE_TYPE.DATE_ONLY,
					color: project.projectColor,
					project: formattedProject,
					isHourEstimated: project.estimationUnit === ESTIMATION_UNIT.HOURS,
					isPlanning: project.status === PROJECT_STATUS.PLANNING || project.status === PROJECT_STATUS.OPPORTUNITY,
					intl: pageComponent.props.intl,
					planningText:
						project.status === PROJECT_STATUS.OPPORTUNITY
							? formatMessage({id: 'project_status.opportunity'})
							: formatMessage({id: 'project_status.planning'}),
					projectGroupId: project.projectGroupId,
					onMoving: pageComponent.onProjectItemMoving.bind(pageComponent),
					onMoveEnd: pageComponent.onProjectItemMoveEnd.bind(pageComponent),
					onMouseEnter: pageComponent.onDistributionItemMouseEnter.bind(
						pageComponent,
						ITEM_TYPE.PROJECT_SCHEDULING_PROJECT,
						project.id,
						project.projectGroupId ? project.id : null
					),
					onMouseMove: pageComponent.onDistributionItemMouseMove.bind(pageComponent),
					onClick: pageComponent.onDistributionItemClick.bind(
						pageComponent,
						ITEM_TYPE.PROJECT_SCHEDULING_PROJECT,
						project.id,
						null
					),
				})
			);
		}
	} else if (response.deleteProject) {
		const project = data.projects.find(project => project.id === response.deleteProject.deletedProjectId);
		if (project) {
			data.projectPersons = data.projectPersons.filter(projectPerson => projectPerson.projectId !== project.id);
			delete data.projectPersonsByProject[project.id];
			data.allocations
				.filter(allocation => allocation.projectId === project.id)
				.forEach(allocationToDelete => {
					const allocationsForPerson = data.allocationsByPerson[allocationToDelete.personId] || [];
					data.allocationsByPerson[allocationToDelete.personId] = allocationsForPerson.filter(
						allocation => allocation.id !== allocationToDelete.id
					);
				});
			data.allocations = data.allocations.filter(allocation => allocation.projectId !== project.id);
			data.timeRegistrations = data.timeRegistrations.filter(
				timeRegistration =>
					timeRegistration.taskId || timeRegistration.idleTimeId || timeRegistration.projectId !== project.id
			);
			data.tasks = data.tasks.filter(task => task.projectId !== project.id);
			const index = data.projects.indexOf(project);
			if (index >= 0) {
				data.projects.splice(index, 1);
			}
			data.projectMap.delete(project.id);
			if (project.projectGroupId) {
				const projectGroupProjects = data.projects.filter(p => p.projectGroupId === project.projectGroupId);
				if (projectGroupProjects.length === 0) {
					const projectGroupIndex = data.projectGroups.findIndex(
						projectGroup => projectGroup.id === project.projectGroupId
					);
					if (projectGroupIndex >= 0) {
						data.projectGroups.splice(projectGroupIndex, 1);
					}
				}
			}
		}
	} else if (response.createPerson) {
		const person = response.createPerson.person.node;
		const addedPerson = {
			id: person.id,
			firstName: person.firstName,
			lastName: person.lastName,
			monday: person.monday,
			tuesday: person.tuesday,
			wednesday: person.wednesday,
			thursday: person.thursday,
			friday: person.friday,
			saturday: person.saturday,
			sunday: person.sunday,
			profilePictureDefaultId: person.profilePictureDefaultId,
			profilePictureId: null,
			roleId: person.role ? person.role.id : null,
			active: true,
			personLabels: [],
		};
		data.persons.push(addedPerson);
		data.personMap.set(person.id, addedPerson);
	}

	dispatch(EVENT_ID.CANVAS_TIMELINE_FORCE_REDRAW, {preventFiltering: false});
	requestAnimationFrame(() => {
		dispatch(EVENT_ID.CANVAS_TIMELINE_FORCE_REDRAW, {preventFiltering: false});
	});
};
